// One catalog, es-AR, loaded at start. Every string a person reads comes
// from here; the lint rule refuses a literal in JSX. Domain nouns (what a
// site, a client or a visit is called) are not in the catalog: they are the
// organization's vocabulary and arrive as interpolation values.
//
// INC-16 (D-421): the product's name lives in one entry, `product.name`, and
// every text that names the product nests it, `$t(product.name)`. What a text
// says under the organization's white label lives in the catalog too, under
// `whiteLabel` and the text's own key; an empty form is something the screen
// leaves out (the product's word at the end of the tab's title, the line at
// the foot of the office's menu). `setWhiteLabel` lays those forms over the
// catalog or takes them away, so no screen asks about the white label itself.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import esAR from '../locales/es-AR/translation.json';

export const DEFAULT_LOCALE = 'es-AR';

/** INC-16: the entry with the product's name, and the branch of the white label's forms. */
export const PRODUCT_NAME = 'product.name';
export const WHITE_LABEL = 'whiteLabel';

interface Tree {
  [key: string]: Tree | string;
}

/** The catalog with the forms laid over it, every key it had kept. */
function overlay(base: Tree, forms: Tree): Tree {
  const result: Tree = { ...base };
  for (const [key, form] of Object.entries(forms)) {
    const under = base[key];
    result[key] =
      typeof form === 'string' || under === undefined || typeof under === 'string'
        ? form
        : overlay(under, form);
  }
  return result;
}

const CATALOG: Tree = esAR;
const FORMS = CATALOG[WHITE_LABEL];

/** A copy of a reading of the catalog, plain JSON as it is. */
function copy(tree: Tree): Tree {
  return JSON.parse(JSON.stringify(tree)) as Tree;
}

/** INC-16: the catalog as an organization under its white label reads it. */
export const WHITE_LABELLED: Tree =
  FORMS === undefined || typeof FORMS === 'string' ? CATALOG : overlay(CATALOG, FORMS);

// The store merges into what it holds: it holds a copy of its own, so the
// catalog and its white-labelled reading above stay as they were written.
void i18n.use(initReactI18next).init({
  resources: { [DEFAULT_LOCALE]: { translation: copy(CATALOG) } },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
  returnNull: false,
  // INC-16: a screen already on view reads the catalog again when the white
  // label changes it.
  react: { bindI18nStore: 'added' },
});

let whiteLabel = false;

/** INC-16 (D-421): the catalog as the organization reads it, under its white
 *  label or not. True when it changed. */
export function setWhiteLabel(on: boolean): boolean {
  if (on === whiteLabel) return false;
  whiteLabel = on;
  const reading = copy(on ? WHITE_LABELLED : CATALOG);
  i18n.addResourceBundle(DEFAULT_LOCALE, 'translation', reading, true, true);
  return true;
}

export default i18n;
